/** Réseaux sociaux (C13): helpers shared by the tabs (roles, refresh, Douala dates, tones). */

import type { ScheduledPostStatusEnum, SocialCommentStatusEnum } from "@agencyos/api-client";
import type { BadgeTone } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";

import { useCurrentRole } from "../../lib/settings";
import { TIME_ZONE } from "../billing/shared";

export const NETWORKS = ["facebook", "instagram"] as const;
export type Network = (typeof NETWORKS)[number];

/** Owner, manager and advisor run the Pages; owner and manager change the moderation. */
export function useSocialRoles() {
  const role = useCurrentRole();
  return {
    role,
    canWork: role === "owner" || role === "manager" || role === "advisor",
    canManage: role === "owner" || role === "manager",
  };
}

export function useRefreshSocial() {
  const queryClient = useQueryClient();
  return () =>
    void queryClient.invalidateQueries({
      predicate: (query) => String(query.queryKey[0] ?? "").startsWith("/api/v1/social/"),
    });
}

export function postTone(status: ScheduledPostStatusEnum): BadgeTone {
  if (status === "published") return "success";
  if (status === "failed") return "danger";
  if (status === "scheduled" || status === "publishing") return "offer";
  return "default";
}

export function commentTone(status: SocialCommentStatusEnum): BadgeTone {
  if (status === "needs_human") return "danger";
  if (status === "hidden") return "offer";
  if (status === "answered" || status === "handled") return "success";
  return "default";
}

/** Day in Douala of an ISO timestamp, as YYYY-MM-DD. */
export function dayInDouala(iso: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: TIME_ZONE }).format(new Date(iso));
}

/** « YYYY-MM » → first and last day, as YYYY-MM-DD. */
export function monthBounds(month: string): { start: string; end: string } {
  const [year, monthIndex] = month.split("-").map(Number);
  const last = new Date(Date.UTC(year ?? 2026, monthIndex ?? 1, 0)).getUTCDate();
  return { start: `${month}-01`, end: `${month}-${String(last).padStart(2, "0")}` };
}

export function shiftMonth(month: string, step: number): string {
  const [year, monthIndex] = month.split("-").map(Number);
  const date = new Date(Date.UTC(year ?? 2026, (monthIndex ?? 1) - 1 + step, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
}

/** A « datetime-local » value (Douala wall clock) of an ISO timestamp. */
export function localInputOf(iso: string | null | undefined): string {
  if (!iso) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(iso));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
}

export function timeText(iso: string, language: string): string {
  return new Date(iso).toLocaleTimeString(language === "en" ? "en-GB" : language, { timeZone: TIME_ZONE, timeStyle: "short" });
}

export function dayText(day: string, language: string): string {
  return new Date(`${day}T12:00:00Z`).toLocaleDateString(language === "en" ? "en-GB" : language, {
    timeZone: TIME_ZONE,
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

export function monthText(month: string, language: string): string {
  return new Date(`${month}-15T12:00:00Z`).toLocaleDateString(language === "en" ? "en-GB" : language, {
    timeZone: TIME_ZONE,
    month: "long",
    year: "numeric",
  });
}

/** Copy to the clipboard; false when the browser refuses (the text stays selectable). */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
